import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Elements, useStripe } from "@stripe/react-stripe-js";
import { supabase } from "../../lib/supabase";
import { stripePromise } from "../../lib/stripe";
import { getChargeableConversionsSummary, payNow, payNowFinalize } from "../../services/stripeCardsService";
import { cardBrandLabel } from "./CardManagementSection";
import { amountLabel, normalizeRow, PaymentRow, PAYMENT_ROW_COLUMNS } from "./paymentFormatting";

const RECENT_PAYMENTS_LIMIT = 5;

// Count presets for the "pay oldest N" picker (DEV-2116 WU-3) — the manual
// payment button no longer takes a dollar amount; the advertiser picks how
// many of the oldest unpaid, chargeable, current-month conversions to pay,
// and the server resolves the exact set and its summed spend.
const COUNT_PRESETS = [10, 25, 50, 100];

// Debounce for the running-total lookup as the advertiser changes count —
// avoids firing one RPC per keystroke on the custom input.
const RUNNING_TOTAL_DEBOUNCE_MS = 300;

// Fallback for the server's own MAX_PENDING_CONVERSION_IDS, which
// chargeable-conversions reports as `max_payable_count`. One pay-now can
// only ever cover that many conversions; the picker must clamp to it so the
// amount the advertiser confirms is the amount actually charged (an
// unclamped "All" on 1,200 unpaid conversions used to promise the full
// balance and silently charge the oldest 500). Only used when talking to an
// older deployment that doesn't report the cap yet.
const DEFAULT_MAX_PAYABLE_COUNT = 500;

// MakePaymentForm — count selector + saved-card picker per Jordan Bentley's
// ask: an advertiser who owes more than one card's limit can pay in pieces,
// picking how many of the oldest unpaid conversions to pay and which card
// each time. The advertiser is present in the browser, so this supports
// interactive 3D Secure if the issuer requires it — payNow either returns a
// terminal result directly (the common case, most saved cards already
// authenticated at SetupIntent time) or a client_secret to complete via
// stripe.confirmCardPayment, followed by payNowFinalize (which always
// re-verifies the real outcome with Stripe server-side rather than trusting
// anything reported client-side).
function MakePaymentForm({ advertiserId, authToken, onPaid, onCancel }) {
  const stripe = useStripe();
  const [cards, setCards] = useState(null); // null = still loading
  const [summary, setSummary] = useState(null); // null = still loading; { unpaid_count, unpaid_total, payable_count }
  const [count, setCount] = useState(null); // resolved once summary loads
  const [customCount, setCustomCount] = useState("");
  // Which control is currently active. Tracked explicitly rather than
  // inferred from `count`, so two controls resolving to the same number
  // (e.g. exactly 25 unpaid: the "25" preset and "All") can never both
  // report aria-pressed="true".
  const [selection, setSelection] = useState(null); // { kind: "preset", value } | { kind: "all" } | { kind: "custom" }
  const [runningTotal, setRunningTotal] = useState(null); // null while (re)computing
  const [totalError, setTotalError] = useState(null);
  const [cardId, setCardId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  // Separate from `error` (which is for a failed *payment attempt*) — a
  // failed *load* must never be indistinguishable from "confirmed 0 unpaid
  // conversions" (see the loadError branch below), or a real fetch failure
  // would silently render as "Nothing owed right now."
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    // Clear stale state from a previous advertiser before loading the new
    // one — otherwise an earlier loadError sticks around through a later
    // successful load, or the form could submit the previous advertiser's
    // selected card while the new request is still pending.
    setCards(null);
    setSummary(null);
    setCount(null);
    setCustomCount("");
    setSelection(null);
    setRunningTotal(null);
    setTotalError(null);
    setCardId("");
    setError(null);
    setLoadError(null);
    if (!supabase || !advertiserId) {
      setCards([]);
      setSummary({ unpaid_count: 0, unpaid_total: 0, payable_count: 0 });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const [cardsResult, summaryResult] = await Promise.all([
          supabase
            .from("advertiser_payment_methods")
            .select("id, brand, last4, is_primary")
            .eq("advertiser_id", advertiserId)
            .order("created_at", { ascending: true }),
          getChargeableConversionsSummary(advertiserId, authToken),
        ]);
        if (cancelled) return;
        if (cardsResult.error) throw new Error(cardsResult.error.message || "Failed to load cards");
        const rows = cardsResult.data ?? [];
        setCards(rows);
        setCardId(rows.find((c) => c.is_primary)?.id ?? rows[0]?.id ?? "");
        const unpaidCount = summaryResult.unpaid_count ?? 0;
        const unpaidTotal = summaryResult.unpaid_total ?? 0;
        const maxPayable = summaryResult.max_payable_count ?? DEFAULT_MAX_PAYABLE_COUNT;
        // What a single payment can actually cover. Everything the picker
        // offers ("All", the presets, the custom input's max) is bounded by
        // this, so the confirmed figure and the charged figure agree.
        const payableCount = Math.min(unpaidCount, maxPayable);
        setSummary({ unpaid_count: unpaidCount, unpaid_total: unpaidTotal, payable_count: payableCount });
        // Default to "All" — the largest amount payable in one go. The
        // displayed total is refreshed by the effect below; the server
        // recomputes the authoritative amount on submit.
        setCount(payableCount);
        setSelection({ kind: "all" });
        setRunningTotal(payableCount === unpaidCount ? unpaidTotal : null);
      } catch (err) {
        if (cancelled) return;
        // Leave cards/summary as null (still "loading") rather than
        // coercing to [] / zeros — those are real, displayable values ("no
        // cards", "nothing owed") that this failure hasn't actually
        // established.
        setLoadError(err?.message || "Failed to load payment details");
      }
    })();
    return () => { cancelled = true; };
  }, [advertiserId, authToken]);

  // Recompute the running total whenever the selected count changes,
  // debounced so typing a custom count doesn't fire one RPC per keystroke.
  useEffect(() => {
    // count <= 0 (an invalid custom entry) deliberately leaves runningTotal
    // null rather than keeping the previous figure — a button reading "Pay
    // $100.00" next to "→ 0 conversions" is a contradiction the submit
    // handler would then reject.
    if (!summary || count == null || count <= 0) return;
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const result = await getChargeableConversionsSummary(advertiserId, authToken, count);
        if (!cancelled) {
          setRunningTotal(result.unpaid_total ?? 0);
          setTotalError(null);
        }
      } catch (err) {
        // Clear the total as well as reporting the failure: an amount we
        // could not confirm must never sit on the button. The submit stays
        // disabled while runningTotal is null, so the error is what explains
        // why — swallowing it would leave the form silently stuck.
        if (!cancelled) {
          setRunningTotal(null);
          setTotalError(err?.message || "Couldn't refresh the total — change the count to retry.");
        }
      }
    }, RUNNING_TOTAL_DEBOUNCE_MS);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [advertiserId, authToken, count, summary]);

  // Every count change invalidates the displayed total immediately. Without
  // this the button kept the PREVIOUS total for the length of the debounce,
  // so clicking "10" then "Pay" straight away confirmed one amount and
  // charged another.
  const changeCount = (next, nextSelection) => {
    setCount(next);
    setSelection(nextSelection);
    setRunningTotal(null);
    setTotalError(null);
  };

  const payableCount = summary?.payable_count ?? 0;

  const selectPreset = (preset) => {
    setCustomCount("");
    changeCount(Math.min(preset, payableCount), { kind: "preset", value: preset });
  };

  const selectAll = () => {
    setCustomCount("");
    changeCount(payableCount, { kind: "all" });
  };

  const handleCustomCountChange = (raw) => {
    setCustomCount(raw);
    const parsed = parseInt(raw, 10);
    if (raw === "") return; // an emptied field leaves the last valid selection in place
    if (Number.isFinite(parsed) && parsed > 0) {
      changeCount(Math.min(parsed, payableCount), { kind: "custom" });
    } else {
      // A non-positive/invalid custom count means "pay nothing" until fixed —
      // and clears the total, so nothing contradictory is left on screen.
      changeCount(0, { kind: "custom" });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe) return;
    if (!count || count <= 0) {
      setError("Select at least one conversion to pay.");
      return;
    }
    if (runningTotal == null) {
      setError("Still working out the total — try again in a moment.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const result = await payNow(advertiserId, authToken, cardId, count);
      if (result.requires_action) {
        // Regardless of what confirmCardPayment itself reports (success,
        // decline, or the advertiser abandoning the challenge), finalize is
        // what actually records the outcome — it re-checks the real status
        // with Stripe rather than trusting this client-side result.
        await stripe.confirmCardPayment(result.client_secret);
        const finalResult = await payNowFinalize(advertiserId, authToken, result.payment_intent_id);
        if (finalResult.success) {
          onPaid();
        } else {
          setError(finalResult.failure_message || "Payment was not completed.");
        }
      } else if (result.success) {
        onPaid();
      } else {
        setError(result.failure_message || "Payment failed.");
      }
    } catch (err) {
      setError(err?.message || "Failed to process payment.");
    } finally {
      setSubmitting(false);
    }
  };

  const loading = !loadError && (cards === null || summary === null);

  return (
    <div style={{ padding:"12px 14px", marginBottom:12, background:"#FFF5FB", border:"1px solid #FFE9F6", borderRadius:10 }}>
      {loadError ? (
        <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
          <div style={{ fontSize:13, color:"#e53935" }}>{loadError}</div>
          <button
            type="button"
            onClick={onCancel}
            style={{ padding:"10px 0", background:"transparent", color:"#626262", border:"1px solid #E2E2E2", borderRadius:10, fontSize:13, cursor:"pointer" }}
          >
            Close
          </button>
        </div>
      ) : loading ? (
        <div style={{ fontSize:12, color:"#7b7b7b" }}>Loading…</div>
      ) : summary.unpaid_count === 0 ? (
        <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
          <div style={{ fontSize:13, color:"#3E3E3E" }}>Nothing owed right now.</div>
          <button
            type="button"
            onClick={onCancel}
            style={{ padding:"10px 0", background:"transparent", color:"#626262", border:"1px solid #E2E2E2", borderRadius:10, fontSize:13, cursor:"pointer" }}
          >
            Close
          </button>
        </div>
      ) : cards.length === 0 ? (
        <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
          <div style={{ fontSize:13, color:"#3E3E3E" }}>Add a card before making a payment.</div>
          <button
            type="button"
            onClick={onCancel}
            style={{ padding:"10px 0", background:"transparent", color:"#626262", border:"1px solid #E2E2E2", borderRadius:10, fontSize:13, cursor:"pointer" }}
          >
            Close
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit}>
          <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
            <div style={{ fontSize:12, color:"#3E3E3E" }}>
              Amount owed this month: <strong>{amountLabel(summary.unpaid_total)}</strong> ({summary.unpaid_count} unpaid conversion{summary.unpaid_count === 1 ? "" : "s"})
            </div>
            {summary.payable_count < summary.unpaid_count && (
              // The cap is surfaced BEFORE the advertiser confirms, never
              // discovered afterwards from a smaller charge than the one
              // they agreed to.
              <div style={{ fontSize:11, color:"#626262" }}>
                Up to {summary.payable_count} conversions can be paid at a time — pay the rest straight after.
              </div>
            )}
            <div style={{ fontSize:11, color:"#626262" }}>
              How many to pay
              <div style={{ display:"flex", gap:6, marginTop:4, flexWrap:"wrap" }}>
                {COUNT_PRESETS.map((preset) => {
                  const active = selection?.kind === "preset" && selection.value === preset;
                  const unavailable = preset > payableCount;
                  return (
                    <button
                      key={preset}
                      type="button"
                      onClick={() => selectPreset(preset)}
                      disabled={submitting || unavailable}
                      aria-pressed={active}
                      style={{
                        padding:"6px 10px", borderRadius:8, fontSize:12, cursor: submitting || unavailable ? "not-allowed" : "pointer",
                        background: active ? "#F90091" : "#FFFFFF",
                        color: active ? "#fff" : "#151515",
                        border:"1px solid #E2E2E2", opacity: unavailable ? 0.5 : 1,
                      }}
                    >
                      {preset}
                    </button>
                  );
                })}
                <button
                  type="button"
                  onClick={selectAll}
                  disabled={submitting}
                  aria-pressed={selection?.kind === "all"}
                  style={{
                    padding:"6px 10px", borderRadius:8, fontSize:12, cursor: submitting ? "not-allowed" : "pointer",
                    background: selection?.kind === "all" ? "#F90091" : "#FFFFFF",
                    color: selection?.kind === "all" ? "#fff" : "#151515",
                    border:"1px solid #E2E2E2",
                  }}
                >
                  All
                </button>
              </div>
            </div>
            <label style={{ fontSize:11, color:"#626262" }}>
              Custom count
              <input
                type="number"
                min="1"
                max={payableCount}
                step="1"
                value={customCount}
                onChange={(e) => handleCustomCountChange(e.target.value)}
                placeholder="Custom"
                disabled={submitting}
                style={{ display:"block", width:"100%", boxSizing:"border-box", marginTop:4, padding:"10px 12px", background:"#FFFFFF", border:"1px solid #E2E2E2", borderRadius:8, fontSize:14, color:"#151515" }}
              />
            </label>
            <div style={{ fontSize:12, color:"#3E3E3E" }}>
              → {count ?? 0} conversion{(count ?? 0) === 1 ? "" : "s"} ·{" "}
              {count > 0 ? (runningTotal == null ? "…" : amountLabel(runningTotal)) : "—"}
            </div>
            {totalError && (
              <div style={{ fontSize:12, color:"#e53935" }}>{totalError}</div>
            )}
            <label style={{ fontSize:11, color:"#626262" }}>
              Card
              <select
                value={cardId}
                onChange={(e) => setCardId(Number(e.target.value))}
                disabled={submitting}
                style={{ display:"block", width:"100%", boxSizing:"border-box", marginTop:4, padding:"10px 12px", paddingRight:32, background:"#FFFFFF", border:"1px solid #E2E2E2", borderRadius:8, fontSize:14, color:"#151515" }}
              >
                {cards.map((c) => (
                  <option key={c.id} value={c.id}>
                    {cardBrandLabel(c.brand)} •••• {c.last4}
                    {c.is_primary ? " (Primary)" : ""}
                  </option>
                ))}
              </select>
            </label>
            {error && (
              <div style={{ fontSize:12, color:"#e53935" }}>{error}</div>
            )}
            <div style={{ display:"flex", gap:8 }}>
              <button
                type="button"
                onClick={onCancel}
                disabled={submitting}
                style={{ flex:1, padding:"10px 0", background:"transparent", color:"#626262", border:"1px solid #E2E2E2", borderRadius:10, fontSize:13, cursor: submitting ? "not-allowed" : "pointer" }}
              >
                Cancel
              </button>
              {/*
                Disabled while the total is unknown (recomputing after a
                count change, a failed refresh, or an invalid count) — the
                label and the charge must always describe the same figure.
              */}
              <button
                type="submit"
                disabled={submitting || !stripe || !count || count <= 0 || runningTotal == null}
                style={{ flex:1, padding:"10px 0", background:"#F90091", color:"#fff", border:"none", borderRadius:10, fontSize:13, fontWeight:600, cursor: submitting ? "wait" : "pointer", opacity: submitting || !count || count <= 0 || runningTotal == null ? 0.7 : 1 }}
              >
                {submitting ? "Processing…" : runningTotal != null ? `Pay ${amountLabel(runningTotal)}` : "Pay"}
              </button>
            </div>
          </div>
        </form>
      )}
    </div>
  );
}

// PaymentHistorySection — ledger of past payment attempts (automated daily
// job + advertiser-initiated manual payments), plus the "Make a payment"
// entry point. Mounted alongside CardManagementSection inside
// BillingMethodSection, same gating (billing_method === 'credit_card').
// advertiser_payments (migration 058) is written only by the daily charge
// job (cycle-scheduled-tasks, source='automatic') and this app's own
// stripe-cards Edge Function (source='manual') — read-only here, under RLS.
export function PaymentHistorySection({ advertiserId, authToken, loading, canManageBilling, onClose, isSupportUser }) {
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPayForm, setShowPayForm] = useState(false);

  const fetchHistory = useCallback(async () => {
    if (!supabase || !advertiserId) return [];
    const { data, error: fetchError } = await supabase
      .from("advertiser_payments")
      .select(PAYMENT_ROW_COLUMNS)
      .eq("advertiser_id", advertiserId)
      .neq("status", "draft") // pre-Stripe intent rows (migration 066) are internal only
      .order("created_at", { ascending: false })
      .limit(RECENT_PAYMENTS_LIMIT);
    if (fetchError) {
      setError(fetchError.message || "Failed to load payment history");
      return [];
    }
    return (data ?? []).map(normalizeRow);
  }, [advertiserId]);

  const refresh = useCallback(async () => {
    const rows = await fetchHistory();
    setHistory(rows);
  }, [fetchHistory]);

  useEffect(() => {
    if (loading) return;
    if (!advertiserId) { setHistoryLoading(false); return; }
    let cancelled = false;
    (async () => {
      setHistoryLoading(true);
      setError(null);
      try {
        const rows = await fetchHistory();
        if (cancelled) return;
        setHistory(rows);
      } catch (err) {
        if (cancelled) return;
        setError(err?.message || "Failed to load payment history");
      } finally {
        if (!cancelled) setHistoryLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [loading, advertiserId, fetchHistory]);

  const handlePaid = async () => {
    setShowPayForm(false);
    await refresh();
  };

  // Closes the surrounding settings panel (UserMenu) before navigating away,
  // if the caller passed one — the panel would otherwise stay open behind
  // the newly-navigated page.
  const handleSeeMore = () => {
    onClose?.();
    navigate("/payments");
  };

  return (
    <>
      <div style={{ borderTop:"1px solid #E2E2E2", margin:"16px 0 12px" }} />
      <div style={{ fontSize:11, color:"#626262", textTransform:"uppercase", letterSpacing:1, marginBottom:8 }}>Recent Payments</div>

      {historyLoading ? (
        <div style={{ fontSize:12, color:"#7b7b7b" }}>Loading…</div>
      ) : (
        <>
          {error && (
            <div style={{ marginBottom:10, fontSize:12, color:"#e53935" }}>{error}</div>
          )}

          {showPayForm && stripePromise && (
            <Elements stripe={stripePromise}>
              <MakePaymentForm
                advertiserId={advertiserId}
                authToken={authToken}
                onPaid={handlePaid}
                onCancel={() => setShowPayForm(false)}
              />
            </Elements>
          )}

          {showPayForm && !stripePromise && (
            <div style={{ marginBottom:10, fontSize:12, color:"#e53935" }}>
              Card payments are not configured for this environment.
            </div>
          )}

          {history.length === 0 ? (
            <div style={{ fontSize:13, color:"#3E3E3E" }}>No charges yet.</div>
          ) : (
            history.map((row) => <PaymentRow key={row.key} row={row} />)
          )}

          {!showPayForm && (
            <div style={{ display:"flex", gap:8, marginTop:12 }}>
              {history.length > 0 && (
                <button
                  type="button"
                  onClick={handleSeeMore}
                  style={{ padding:"10px 20px", background:"#F90091", color:"#fff", border:"none", borderRadius:10, fontSize:13, fontWeight:600, cursor:"pointer" }}
                >
                  See more
                </button>
              )}
              {canManageBilling && !isSupportUser && (
                // Masquerade is read-only by convention (CLAUDE.md) — canManageBilling
                // alone is true for support (their own billing_enabled check is
                // bypassed), but stripe-cards' pay-now/pay-now-finalize actions
                // unconditionally reject support callers server-side. Gating the
                // button the same way avoids offering an action that always 403s.
                <button
                  type="button"
                  onClick={() => setShowPayForm(true)}
                  style={{ padding:"10px 20px", background:"#F90091", color:"#fff", border:"none", borderRadius:10, fontSize:13, fontWeight:600, cursor:"pointer" }}
                >
                  Make a payment
                </button>
              )}
            </div>
          )}
        </>
      )}
    </>
  );
}
